import { useRef } from "react";
import { MAIL_COMPLAINT_RATE_FLAG } from "@feedback-hub/contract/browser";

import { MAIL_TABS, MAIL_TAB_LABELS, type MailTab } from "../mailTabs.js";
import { DashboardTab } from "./mail/DashboardTab.js";
import { EmailsTab } from "./mail/EmailsTab.js";
import { AppStatsTab } from "./mail/AppStatsTab.js";
import { RecipientsTab } from "./mail/RecipientsTab.js";
import { SuppressionsTab } from "./mail/SuppressionsTab.js";

function TabBody({ tab }: { tab: MailTab }) {
  switch (tab) {
    case "dashboard":
      return <DashboardTab />;
    case "emails":
      return <EmailsTab />;
    case "apps":
      return <AppStatsTab />;
    case "recipients":
      return <RecipientsTab />;
    case "suppressions":
      return <SuppressionsTab />;
  }
}

export interface MailProps {
  tab: MailTab;
  onSelectTab: (tab: MailTab) => void;
}

export function Mail({ tab, onSelectTab }: MailProps) {
  const tabRefs = useRef<Partial<Record<MailTab, HTMLButtonElement | null>>>({});

  function move(to: MailTab) {
    onSelectTab(to);
    tabRefs.current[to]?.focus();
  }

  function onKeyDown(e: React.KeyboardEvent) {
    const i = MAIL_TABS.indexOf(tab);
    const n = MAIL_TABS.length;
    let target: MailTab | null = null;
    if (e.key === "ArrowRight") target = MAIL_TABS[(i + 1) % n]!;
    else if (e.key === "ArrowLeft") target = MAIL_TABS[(i - 1 + n) % n]!;
    else if (e.key === "Home") target = MAIL_TABS[0]!;
    else if (e.key === "End") target = MAIL_TABS[n - 1]!;
    if (target) {
      e.preventDefault();
      move(target);
    }
  }

  return (
    <div>
      <h2 style={{ margin: "0 0 0.75rem", fontSize: "1.1rem" }}>Mail gateway</h2>
      <p className="muted" style={{ marginTop: 0 }}>
        Every SES send across every instrumented app. A window at or above{" "}
        {(MAIL_COMPLAINT_RATE_FLAG * 100).toFixed(2)}% complaint rate is flagged.
      </p>

      <div className="tabs" role="tablist" aria-label="Mail views" onKeyDown={onKeyDown}>
        {MAIL_TABS.map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            className="tab"
            id={`mail-tab-${t}`}
            aria-selected={t === tab}
            aria-controls="mail-panel"
            tabIndex={t === tab ? 0 : -1}
            ref={(el) => {
              tabRefs.current[t] = el;
            }}
            onClick={() => onSelectTab(t)}
          >
            {MAIL_TAB_LABELS[t]}
          </button>
        ))}
      </div>

      <div role="tabpanel" id="mail-panel" aria-labelledby={`mail-tab-${tab}`}>
        <TabBody tab={tab} />
      </div>
    </div>
  );
}
